// Performance monitoring for Pinia stores
class PiniaPerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.longRunningThreshold = 16; // 16ms = 1 frame at 60fps
    this.isMonitoring = true;
    this.startTime = Date.now();
  }

  // Wrap Pinia actions with performance monitoring
  wrapAction(store, actionName, originalAction) {
    const self = this;
    return async function(...args) {
      if (!self.isMonitoring) return originalAction.apply(store, args);

      const startTime = performance.now();
      const startMark = `${store.$id}-${actionName}-start`;
      const endMark = `${store.$id}-${actionName}-end`;
      
      performance.mark(startMark);
      
      try {
        const result = await originalAction.apply(store, args);
        
        const endTime = performance.now();
        performance.mark(endMark);
        
        const duration = endTime - startTime;
        self.recordMetric(store.$id, actionName, duration, true);
        
        // Log slow operations
        if (duration > self.longRunningThreshold) {
          log.info(1,`🐌 Slow Pinia action: ${store.$id}.${actionName} took ${duration.toFixed(2)}ms`);
        }
        
        return result;
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        self.recordMetric(store.$id, actionName, duration, false);
        throw error;
      }
    };
  }

  recordMetric(storeId, actionName, duration, success) {
    const key = `${storeId}.${actionName}`;
    
    if (!this.metrics.has(key)) {
      this.metrics.set(key, {
        calls: 0,
        totalTime: 0,
        maxTime: 0,
        minTime: Infinity,
        errors: 0,
        longRunning: 0
      });
    }
    
    const metric = this.metrics.get(key);
    metric.calls++;
    metric.totalTime += duration;
    metric.maxTime = Math.max(metric.maxTime, duration);
    metric.minTime = Math.min(metric.minTime, duration);
    
    if (!success) metric.errors++;
    if (duration > this.longRunningThreshold) metric.longRunning++;
  }

  getReport() {
    const report = [];
    
    for (const [key, metric] of this.metrics.entries()) {
      report.push({
        action: key,
        avgTime: (metric.totalTime / metric.calls).toFixed(2),
        maxTime: metric.maxTime.toFixed(2),
        minTime: metric.minTime === Infinity ? 0 : metric.minTime.toFixed(2),
        calls: metric.calls,
        errors: metric.errors,
        longRunning: metric.longRunning,
        totalTime: metric.totalTime.toFixed(2)
      });
    }
    
    return report.sort((a, b) => parseFloat(b.avgTime) - parseFloat(a.avgTime));
  }

  printReport() {
    const report = this.getReport();
    if (report.length === 0) {
      log.info(5,'📊 No Pinia actions have been monitored yet. Make sure stores are wrapped and actions are being called.');
      return;
    }
    console.table(report);
  }

  reset() {
    this.metrics.clear();
    performance.clearMarks();
    performance.clearMeasures();
  }

  // Helper method to manually wrap a specific store
  wrapSpecificStore(store) {
    if (!store || !store.$id) {
      log.info(1,'❌ Invalid store provided');
      return false;
    }
    
    log.info(5,`🔧 Manually wrapping store: ${store.$id}`);
    wrapStoreActions(store, this);
    return true;
  }

  // Method to wrap only specific methods in specific stores
  wrapSpecificMethods(storeMethodMap) {
    log.info(5,'🎯 Wrapping specific store methods...');
    
    // storeMethodMap should be an object like:
    // {
    //   'anns': ['setMap', 'updateActive', 'resetActive'],
    //   'playback': ['personalizeInteraction', 'setConditionalStatus']
    // }
    
    for (const [storeId, methods] of Object.entries(storeMethodMap)) {
      const store = this.findStoreById(storeId);
      
      if (!store) {
        log.info(1,`❌ Store '${storeId}' not found`);
        continue;
      }
      
      log.info(5,`🔧 Wrapping ${methods.length} methods in store '${storeId}'`);
      
      methods.forEach(methodName => {
        if (typeof store[methodName] === 'function') {
          const originalMethod = store[methodName];
          store[methodName] = this.wrapAction(store, methodName, originalMethod);
          log.info(5,`✅ Wrapped ${storeId}.${methodName}`);
        } else {
          log.info(1,`❌ Method '${methodName}' not found in store '${storeId}'`);
        }
      });
    }
  }

  // Helper to find a store by its ID
  findStoreById(storeId) {
    try {
      // Try to find in global Pinia instance
      const app = window.Vue?.config?.globalProperties || 
                  document.querySelector('#app').__vue_app__?.config?.globalProperties;
      
      if (app?.$pinia?._s) {
        for (const [key, store] of app.$pinia._s.entries()) {
          if (store.$id === storeId) {
            return store;
          }
        }
      }
      
      // Alternative: try window globals (if stores are exposed)
      const globalStore = window[storeId] || window[`${storeId}Store`];
      if (globalStore && globalStore.$id === storeId) {
        return globalStore;
      }
      
    } catch (error) {
      log.info(1,`Error finding store '${storeId}':`, error);
    }
    
    return null;
  }

  // Convenience method for your specific use case
  wrapTargetMethods() {
    this.wrapSpecificMethods({
      'anns': [
        'setMap', 
        'updateActive', 
        'resetActive', 
        'personalizeInteraction', 
        'setConditionalStatus', 
        'handleVariableChange', 
        'annotationsAtTime', 
        'updateAllConditionalStatuses',
      ],
    });
  }
}

// Manual-only Pinia monitoring setup
function setupPiniaMonitoring(app, options = {}) {
  const monitor = new PiniaPerformanceMonitor();
  
  // Expose monitor globally for debugging immediately
  window.piniaMonitor = monitor;
  
  // Only run automatic wrapping if explicitly requested
  if (options.autoWrap !== false && options.manualOnly !== true) {
    log.info(5,'🔍 Setting up automatic Pinia monitoring...');
    setupAutomaticStoreMonitoring(monitor, app);
  } else {
    log.info(5,'🎯 Manual-only Pinia monitoring enabled');
  }
  
  return monitor;
}

// Separate function for automatic monitoring (only called if requested)
function setupAutomaticStoreMonitoring(monitor, app) {
  // Prevent multiple setups
  if (app._piniaMonitoringSetup) {
    log.info(5,'⚠️ Pinia monitoring already setup, skipping...');
    return monitor;
  }
  app._piniaMonitoringSetup = true;
  
  log.info(5,'🔍 Setting up automatic Pinia monitoring...');
  
  // Method 1: Try to intercept Pinia plugin installation
  const originalUse = app.use;
  let piniaInstance = null;
  
  app.use = function(plugin, ...args) {
    const result = originalUse.call(this, plugin, ...args);
    
    // Detect Pinia installation (be more specific)
    if (plugin && plugin.install && plugin.install.toString().includes('pinia')) {
      if (!piniaInstance) {
        log.info(5,'✅ Detected Pinia plugin installation');
        piniaInstance = plugin;
        
        // Set up store monitoring after a longer delay to ensure stores are created
        setTimeout(() => {
          setupStoreMonitoring(monitor, app);
        }, 2000);
      }
    }
    
    return result;
  };
  
  // Method 2: More aggressive fallback - keep trying to find stores
  let attempts = 0;
  const maxAttempts = 20;
  
  const findStoresInterval = setInterval(() => {
    attempts++;
    
    const pinia = app.config.globalProperties.$pinia || 
                 app._context?.provides?.pinia ||
                 window.pinia;
    
    if (pinia && pinia._s && pinia._s.size > 0) {
      log.info(5,`✅ Found Pinia with ${pinia._s.size} stores on attempt ${attempts}`);
      setupStoreMonitoring(monitor, app);
      clearInterval(findStoresInterval);
    } else if (attempts >= maxAttempts) {
      log.info(1,'❌ Could not find Pinia stores after', maxAttempts, 'attempts');
      clearInterval(findStoresInterval);
    } else {
      log.info(5,`🔍 Attempt ${attempts}: Looking for stores...`);
    }
  }, 500);
}

function setupStoreMonitoring(monitor, app) {
  log.info(5,'🚀 Setting up Pinia store monitoring...');
  
  // Prevent multiple setups
  if (monitor._storeMonitoringSetup) {
    log.info(5,'⚠️ Store monitoring already setup, skipping...');
    return;
  }
  monitor._storeMonitoringSetup = true;
  
  try {
    // Get Pinia instance
    const pinia = app.config.globalProperties.$pinia || 
                 app._context?.provides?.pinia ||
                 window.pinia;
    
    if (!pinia) {
      log.info(1,'❌ Could not find Pinia instance');
      return;
    }
    
    // Get existing stores
    const stores = pinia._s || new Map();
    log.info(5,`📊 Found ${stores.size} existing stores`);
    
    if (stores.size === 0) {
      log.info(5,'⏳ No stores found yet, will keep monitoring...');
    }
    
    const wrappedStores = new WeakSet();
    
    // Function to wrap a single store
    const wrapStore = (store) => {
      if (!wrappedStores.has(store)) {
        log.info(5,`🔧 Wrapping store: ${store.$id}`);
        wrapStoreActions(store, monitor);
        wrappedStores.add(store);
      }
    };
    
    // Wrap existing stores
    stores.forEach(wrapStore);
    
    // Monitor for new stores being created
    const originalStoreSet = stores.set;
    if (originalStoreSet) {
      stores.set = function(key, store) {
        const result = originalStoreSet.call(this, key, store);
        
        setTimeout(() => {
          log.info(5,`🔧 New store detected: ${store.$id}`);
          wrapStore(store);
        }, 10);
        
        return result;
      };
    }
    
    // Also periodically check for new stores (fallback)
    const pollInterval = setInterval(() => {
      let newStoresFound = false;
      const currentStores = pinia._s || new Map();
      
      currentStores.forEach((store) => {
        if (!wrappedStores.has(store)) {
          log.info(5,`🔧 Found and wrapping new store: ${store.$id}`);
          wrapStore(store);
          newStoresFound = true;
        }
      });
      
      // Stop polling after 30 seconds if no new stores
      if (!newStoresFound && Date.now() > monitor.startTime + 30000) {
        clearInterval(pollInterval);
        log.info(5,'🏁 Stopped polling for new stores');
      }
    }, 1000);
    
    // Also expose a manual method to wrap stores
    window.piniaMonitor.wrapAllStores = () => {
      const currentStores = pinia._s || new Map();
      log.info(5,`🔧 Manually wrapping ${currentStores.size} stores...`);
      currentStores.forEach(wrapStore);
    };
    
    log.info(5,'✅ Store monitoring setup complete');
    
  } catch (error) {
    log.info(1,'❌ Error setting up store monitoring:', error);
  }
}

function wrapStoreActions(store, monitor) {
  if (!store || !store.$id) {
    log.info(1,'❌ Invalid store provided for wrapping');
    return;
  }
  
  try {
    log.info(5,`🔧 Wrapping actions for store: ${store.$id}`);
    
    // Get all methods from the store instance and its prototype
    const allMethods = new Set();
    
    // Get methods from the store instance itself
    Object.getOwnPropertyNames(store).forEach(key => {
      if (typeof store[key] === 'function' && 
          key !== 'constructor' && 
          !key.startsWith('$') &&
          !key.startsWith('_')) {
        allMethods.add(key);
      }
    });
    
    // Get methods from the prototype chain
    let proto = Object.getPrototypeOf(store);
    while (proto && proto !== Object.prototype) {
      Object.getOwnPropertyNames(proto).forEach(key => {
        if (typeof proto[key] === 'function' && 
            key !== 'constructor' && 
            !key.startsWith('$') &&
            !key.startsWith('_')) {
          allMethods.add(key);
        }
      });
      proto = Object.getPrototypeOf(proto);
    }
    
    log.info(5,`📝 Found ${allMethods.size} methods in ${store.$id}:`, Array.from(allMethods));
    
    // Wrap each method
    allMethods.forEach(methodName => {
      try {
        const originalMethod = store[methodName];
        if (typeof originalMethod === 'function') {
          store[methodName] = monitor.wrapAction(store, methodName, originalMethod);
          log.info(5,`✅ Wrapped ${store.$id}.${methodName}`);
        }
      } catch (error) {
        log.info(1,`❌ Failed to wrap ${store.$id}.${methodName}:`, error);
      }
    });
    
    // Also wrap $patch if it exists
    if (store.$patch && typeof store.$patch === 'function') {
      const original$patch = store.$patch;
      store.$patch = monitor.wrapAction(store, '$patch', original$patch);
      log.info(5,`✅ Wrapped ${store.$id}.$patch`);
    }
    
    // Add a test method to verify wrapping is working
    store._testMonitoring = function() {
      log.info(5,`🧪 Test method called on ${this.$id}`);
      // Do some work to generate metrics
      const start = performance.now();
      let result = 0;
      for (let i = 0; i < 10000; i++) {
        result += Math.sqrt(i);
      }
      const duration = performance.now() - start;
      log.info(5,`🧪 Test completed in ${duration.toFixed(2)}ms`);
      return result;
    };
    
    // Wrap the test method too
    const originalTest = store._testMonitoring;
    store._testMonitoring = monitor.wrapAction(store, '_testMonitoring', originalTest);
    
  } catch (error) {
    log.info(1,`❌ Error wrapping store ${store.$id}:`, error);
  }
}

export { PiniaPerformanceMonitor, setupPiniaMonitoring };